<script lang="ts">
  import { onMount } from 'svelte';
  import HeroModule from '$src/js/modules/hero/HeroModule';
  import { HeroState } from '$src/js/states/HeroState.svelte';
  import Button from '$src/js/components/molecules/button/Button.svelte';
  import BaseModule from '$src/js/modules/base/BaseModule';

  let overlayEl = $state<HTMLDivElement>(undefined!);
  let contentEl = $state<HTMLDivElement>(undefined!);
  let progressFillEl = $state<HTMLDivElement>(undefined!);
  let showButton = $state(false);
  let pageLoaded = $state(false);
  const progressState = { pendingTarget: 0, currentTarget: 0, isAnimating: false };

  // Animate to the target progress
  const animateToTarget = (): void => {
    const target = progressState.pendingTarget;

    if (target === progressState.currentTarget) {
      progressState.isAnimating = false;

      if (target >= 1) {
        setTimeout(() => {
          showButton = true;
        }, 500);
      }
      return;
    }

    progressState.isAnimating = true;
    progressState.currentTarget = target;
    progressFillEl.style.transform = `scaleX(${String(target)})`;
  };

  // On transition end, animate to the next target
  const handleTransitionEnd = (e: TransitionEvent): void => {
    if (e.propertyName !== 'transform') {
      return;
    }

    animateToTarget();
  };

  // Listen to transition end events
  $effect(() => {
    const el = progressFillEl;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!el) {
      return;
    }

    el.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      el.removeEventListener('transitionend', handleTransitionEnd);
    };
  });

  onMount(() => {
    const videoWrapperEl = HeroModule.getInstance().intro.getVideoWrapperEl();
    const headerEl = document.querySelector<HTMLElement>('.header-content');
    const controlsEl = document.querySelector<HTMLElement>('.controls-content');

    if (!videoWrapperEl || !headerEl || !controlsEl) {
      console.error('IntroService: missing required elements');
      return;
    }

    HeroModule.getInstance().intro.setElements({
      contentEl,
      overlayEl,
      videoWrapperEl,
      headerEl,
      controlsEl,
    });

    HeroModule.getInstance().intro.setInitialVideoScale();

    requestAnimationFrame(() => {
      setTimeout(() => {
        pageLoaded = true;
      }, 200);
    });

    // Listen to loader progress
    HeroModule.getInstance().loader.onProgress((progress, complete) => {
      progressState.pendingTarget = complete ? 1 : progress;
      if (!progressState.isAnimating) {
        animateToTarget();
      }
    });
  });
</script>

{#if HeroState.introVisible}
  <div class="fixed inset-0 z-100 flex items-center justify-center bg-black" bind:this={overlayEl}>
    <div
      class="flex w-full max-w-[744px] flex-col items-center p-4 text-balance opacity-0 transition-opacity duration-700 ease-in-out"
      class:opacity-100={pageLoaded}
      bind:this={contentEl}
    >
      <img
        src="https://yellow-b-global.b-cdn.net/production/the-architect/images/logos/logo-tvl-yellow.svg"
        alt="TVL Logo"
        class="absolute top-10 left-1/2 h-18 -translate-x-1/2 md:h-24"
      />

      <img
        src="https://yellow-b-global.b-cdn.net/production/the-architect/images/logos/logo-the-architect-1.svg"
        alt="The Architect"
        class="h-24 md:h-36"
      />

      <p
        class="font-clorat text-yellow text-center text-[48px] leading-[100%] max-md:mt-2 md:text-[80px]"
      >
        {BaseModule.getInstance().i18n.translate('intro_title')}
      </p>

      <div class="relative mt-12 flex w-[360px] max-w-[60%] justify-center md:mt-24">
        <div
          class="absolute top-1/2 h-[4px] w-full -translate-y-1/2 overflow-hidden bg-white/20 transition-opacity duration-500"
          class:opacity-0={showButton}
        >
          <div
            class="bg-yellow h-full w-full origin-left transform-[scaleX(0)] transition-all duration-500 ease-in-out"
            bind:this={progressFillEl}
          ></div>
        </div>

        <div
          class="invisible opacity-0 transition-opacity delay-500 duration-500"
          class:visible={showButton}
          class:opacity-100={showButton}
        >
          <Button
            size="medium"
            text={BaseModule.getInstance().i18n.translate('intro_button')}
            onclick={() => HeroModule.getInstance().intro.dismiss()}
            className="gradient-border--animated"
          />
        </div>
      </div>
    </div>
  </div>
{/if}
